import * as React from 'react';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import StyledTable from '../../components/StyledTable';
import { ResponsiveBar } from '@nivo/bar';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import styled from 'styled-components';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { WaitingListHistoryData, WaitingListPriority, WaitingListRemovalReason } from '../../models';
import { getWaitingListHistoryAsync } from '../../ajax/requests';
import { DateRangeSelector, DateRangeSelectorState } from '../../components/DateRangeSelector';
import { useGeneralSettings } from '../../store/settings/hooks';
import { serviceName } from '../../shared/appointmint/appointment';
import Typography from '../../components/Typography';
import { KpiRow, KpiCard, KpiValue, KpiLabel } from './WaitingListKpiCards';
import AppButton from '../../components/AppButton';

const Container = styled.div`
    padding-top: var(--spacer-S);
`;

const DateRangeRow = styled.div`
    display: flex;
    margin-bottom: var(--spacer-M);
`;

const ChartSection = styled.div`
    margin-bottom: var(--spacer-M);
`;

const ChartTitle = styled.h5`
    margin-bottom: var(--spacer-S);
`;

const ExportRow = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: var(--spacer-S);
`;

const SortableHeader = styled.th`
    cursor: pointer;
    user-select: none;
    &:hover {
        background-color: var(--bg-hover-light);
    }
`;

type SortField = 'clientName' | 'service' | 'professionalName' | 'durationDays' | 'removedAt' | 'priority';

const WaitingListInsights: FC = () => {
    const t = useTranslations();
    const generalSettings = useGeneralSettings();

    const [dateRange, setDateRange] = useState<DateRangeSelectorState | null>(null);
    const [history, setHistory] = useState<WaitingListHistoryData[]>([]);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [sortField, setSortField] = useState<SortField>('removedAt');
    const [sortAsc, setSortAsc] = useState(false);

    const loadHistory = useCallback((range: DateRangeSelectorState) => {
        setLoading(true);
        setError(false);
        getWaitingListHistoryAsync(undefined, undefined, range.from, range.to)
            .then(data => {
                setHistory(data);
                setLoaded(true);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!dateRange) return;
        loadHistory(dateRange);
    }, [dateRange, loadHistory]);

    const handleSort = useCallback((field: SortField) => {
        setSortAsc(prev => sortField === field ? !prev : true);
        setSortField(field);
    }, [sortField]);

    const sortedHistory = useMemo(() => {
        const sorted = [...history].sort((a, b) => {
            const aVal = a[sortField] ?? '';
            const bVal = b[sortField] ?? '';
            if (typeof aVal === 'number' && typeof bVal === 'number') return aVal - bVal;
            return String(aVal).localeCompare(String(bVal));
        });
        return sortAsc ? sorted : sorted.reverse();
    }, [history, sortField, sortAsc]);

    const { totalResolved, avgWaitTime, longestWait, appointmentCreatedPct, manualPct } = useMemo(() => {
        const total = history.length;
        const appointmentCreatedCount = history.filter(h => h.removalReason === WaitingListRemovalReason.AppointmentCreated).length;
        return {
            totalResolved: total,
            avgWaitTime: total > 0
                ? (history.reduce((sum, h) => sum + h.durationDays, 0) / total).toFixed(1)
                : '0',
            longestWait: total > 0
                ? Math.max(...history.map(h => h.durationDays)).toFixed(1)
                : '0',
            appointmentCreatedPct: total > 0 ? Math.round(appointmentCreatedCount / total * 100) : 0,
            manualPct: total > 0 ? Math.round((total - appointmentCreatedCount) / total * 100) : 0,
        };
    }, [history]);

    const entriesByService = useMemo(() => {
        const map: Record<string, number> = {};
        history.forEach(h => {
            const name = serviceName(h.service, generalSettings);
            map[name] = (map[name] || 0) + 1;
        });
        return Object.entries(map)
            .map(([service, count]) => ({ service, count }))
            .sort((a, b) => b.count - a.count);
    }, [history, generalSettings]);

    const avgWaitByService = useMemo(() => {
        const map: Record<string, { sum: number; count: number }> = {};
        history.forEach(h => {
            const name = serviceName(h.service, generalSettings);
            if (!map[name]) map[name] = { sum: 0, count: 0 };
            map[name].sum += h.durationDays;
            map[name].count++;
        });
        return Object.entries(map)
            .map(([service, { sum, count }]) => ({ service, avgDays: Math.round(sum / count * 10) / 10 }))
            .sort((a, b) => b.avgDays - a.avgDays);
    }, [history, generalSettings]);

    const entriesByProfessional = useMemo(() => {
        const map: Record<string, number> = {};
        history.forEach(h => {
            const name = h.professionalName || '—';
            map[name] = (map[name] || 0) + 1;
        });
        return Object.entries(map)
            .map(([professional, count]) => ({ professional, count }))
            .sort((a, b) => b.count - a.count);
    }, [history]);

    const chartLeftMargin = useMemo(() => {
        const maxServiceLabel = entriesByService.reduce((max, entry) => Math.max(max, entry.service.length), 0);
        const maxProfessionalLabel = entriesByProfessional.reduce((max, entry) => Math.max(max, entry.professional.length), 0);
        const maxLabelLength = Math.max(maxServiceLabel, maxProfessionalLabel);
        return Math.max(100, maxLabelLength * 7);
    }, [entriesByService, entriesByProfessional]);

    const getPriorityLabel = useCallback((priority: WaitingListPriority) => {
        switch (priority) {
            case WaitingListPriority.High: return t(TK.priorityHigh);
            case WaitingListPriority.Urgent: return t(TK.priorityUrgent);
            default: return t(TK.priorityNormal);
        }
    }, [t]);

    const getReasonLabel = useCallback((reason: WaitingListRemovalReason) => {
        switch (reason) {
            case WaitingListRemovalReason.AppointmentCreated: return t(TK.removalReasonAppointmentCreated);
            default: return t(TK.removalReasonManual);
        }
    }, [t]);

    const csvData = useMemo(() => sortedHistory.map(h => ({
        [t(TK.client)]: h.clientName ?? '',
        [t(TK.service)]: serviceName(h.service, generalSettings),
        [t(TK.professional)]: h.professionalName ?? '',
        [t(TK.priority)]: getPriorityLabel(h.priority),
        [t(TK.addedAt)]: h.addedAt ? moment(h.addedAt).format('DD/MM/YYYY') : '',
        [t(TK.removedAt)]: h.removedAt ? moment(h.removedAt).format('DD/MM/YYYY') : '',
        [t(TK.durationDays)]: h.durationDays.toFixed(1),
        [t(TK.removalReason)]: getReasonLabel(h.removalReason),
    })), [sortedHistory, t, generalSettings, getPriorityLabel, getReasonLabel]);

    const chartHeight = (entries: number) => Math.max(200, 50 + entries * 30);

    return (
        <Container>
            <DateRangeRow>
                <DateRangeSelector
                    onRangeChanged={setDateRange}
                    defaultType="month"
                />
            </DateRangeRow>

            {error ? (
                <Typography><i>{t(TK.errorLoadingData)}</i></Typography>
            ) : !loaded ? null : loading ? (
                <Typography><i>{t(TK.loading)}...</i></Typography>
            ) : !history.length ? (
                <Typography><i>{t(TK.noEntries)}</i></Typography>
            ) : (
                <>
                    <KpiRow>
                        <KpiCard>
                            <KpiValue>{totalResolved}</KpiValue>
                            <KpiLabel>{t(TK.entriesResolved)}</KpiLabel>
                        </KpiCard>
                        <KpiCard>
                            <KpiValue>{avgWaitTime}</KpiValue>
                            <KpiLabel>{t(TK.averageWaitTime)} ({t(TK.days)})</KpiLabel>
                        </KpiCard>
                        <KpiCard>
                            <KpiValue>{longestWait}</KpiValue>
                            <KpiLabel>{t(TK.longestWaitTime)} ({t(TK.days)})</KpiLabel>
                        </KpiCard>
                        <KpiCard>
                            <KpiValue>{appointmentCreatedPct}% / {manualPct}%</KpiValue>
                            <KpiLabel>{t(TK.removalReasonAppointmentCreated)} / {t(TK.removalReasonManual)}</KpiLabel>
                        </KpiCard>
                    </KpiRow>

                    <ChartSection>
                        <ChartTitle>{t(TK.entriesByService)}</ChartTitle>
                        <div style={{ height: chartHeight(entriesByService.length) }}>
                            <ResponsiveBar
                                data={entriesByService}
                                keys={['count']}
                                indexBy="service"
                                layout="horizontal"
                                margin={{ top: 10, right: 20, bottom: 40, left: chartLeftMargin }}
                                padding={0.3}
                                colors={{ scheme: 'nivo' }}
                                enableGridX={true}
                                labelFormat={v => `${v}`}
                                tooltipFormat={v => `${v}`}
                            />
                        </div>
                    </ChartSection>

                    <ChartSection>
                        <ChartTitle>{t(TK.averageWaitTimeByService)}</ChartTitle>
                        <div style={{ height: chartHeight(avgWaitByService.length) }}>
                            <ResponsiveBar
                                data={avgWaitByService}
                                keys={['avgDays']}
                                indexBy="service"
                                layout="horizontal"
                                margin={{ top: 10, right: 20, bottom: 40, left: chartLeftMargin }}
                                padding={0.3}
                                colors={{ scheme: 'nivo' }}
                                enableGridX={true}
                                labelFormat={v => `${v}`}
                                tooltipFormat={v => `${v} ${t(TK.days)}`}
                            />
                        </div>
                    </ChartSection>

                    <ChartSection>
                        <ChartTitle>{t(TK.entriesByProfessional)}</ChartTitle>
                        <div style={{ height: chartHeight(entriesByProfessional.length) }}>
                            <ResponsiveBar
                                data={entriesByProfessional}
                                keys={['count']}
                                indexBy="professional"
                                layout="horizontal"
                                margin={{ top: 10, right: 20, bottom: 40, left: chartLeftMargin }}
                                padding={0.3}
                                colors={{ scheme: 'nivo' }}
                                enableGridX={true}
                                labelFormat={v => `${v}`}
                                tooltipFormat={v => `${v}`}
                            />
                        </div>
                    </ChartSection>

                    <ExportRow>
                        <CSVLink
                            data={csvData}
                            separator=";"
                            filename="waiting-list-history.csv"
                        >
                            <AppButton color="secondary">{t(TK.exportData)}</AppButton>
                        </CSVLink>
                    </ExportRow>

                    <StyledTable scrollable>
                        <thead>
                            <tr>
                                <SortableHeader onClick={() => handleSort('clientName')}>{t(TK.client)}</SortableHeader>
                                <SortableHeader onClick={() => handleSort('service')}>{t(TK.service)}</SortableHeader>
                                <SortableHeader onClick={() => handleSort('professionalName')}>{t(TK.professional)}</SortableHeader>
                                <SortableHeader onClick={() => handleSort('priority')}>{t(TK.priority)}</SortableHeader>
                                <th>{t(TK.addedAt)}</th>
                                <SortableHeader onClick={() => handleSort('removedAt')}>{t(TK.removedAt)}</SortableHeader>
                                <SortableHeader onClick={() => handleSort('durationDays')}>{t(TK.durationDays)}</SortableHeader>
                                <th>{t(TK.removalReason)}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedHistory.map(item => (
                                <tr key={item.id}>
                                    <td>{item.clientName ?? '—'}</td>
                                    <td>{serviceName(item.service, generalSettings)}</td>
                                    <td>{item.professionalName ?? '—'}</td>
                                    <td>{getPriorityLabel(item.priority)}</td>
                                    <td>{item.addedAt ? moment(item.addedAt).format('DD/MM/YYYY') : '—'}</td>
                                    <td>{item.removedAt ? moment(item.removedAt).format('DD/MM/YYYY') : '—'}</td>
                                    <td>{item.durationDays.toFixed(1)}</td>
                                    <td>{getReasonLabel(item.removalReason)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </StyledTable>
                </>
            )}
        </Container>
    );
};

export default WaitingListInsights;
